import { useCallback, useEffect, useMemo } from 'react';
import { AssetType, ObservableLevelsEnum } from '../../data/enums';
import useMergedState from '../../hooks/useMergedState';
import { EquipmentPartModel } from '../../data/types';
import DatabaseAssetsMethods from '../../data/database.assets.methods';
import { inspectionDB } from '../../data/database.structure';
import { observationService } from '../../services/observationService';
import DateHelpers from '../../helpers/dateHelpers';
import { ObservableItemIdentifier } from '../../api-contracts/other/ObservableItemIdentifier';

interface SliderRollerState {
  error?: string;
  assetType?: AssetType;
}

const { Undercarriage } = ObservableItemIdentifier.Identifiers;

export const SliderRollerAssemblyConstants = {
  submit: 'Done',
  pathLike: 'Undercarriage.Roller.WearIndicators.',
  completedPath: Undercarriage.RollerAssemblies.Slider.Completed,
  partName: 'Slider',
};

export const useSliderRollerHook = ({
  equipmentPart,
  inspectionCategoryTypeId,
  externalId,
}: {
  externalId?: string;
  equipmentPart?: EquipmentPartModel;
  inspectionCategoryTypeId?: string;
}) => {
  const [state, setState] = useMergedState<SliderRollerState>({});

  useEffect(() => {
    setState({ error: undefined }); // reset error
    const getInspection = async () => {
      try {
        if (!externalId) {
          throw new Error('Inspection data not found');
        }
        const assetMethods = new DatabaseAssetsMethods(inspectionDB);

        const asset = await assetMethods.getAssetByInspectionId(externalId!);

        if (!asset) {
          throw new Error('Asset not found');
        }
        setState({
          assetType: asset.model.assetType as 'ERS' | 'HEX',
          error: undefined,
        });
      } catch (e) {
        setState({ error: e as string });
      }
    };
    getInspection();
  }, [externalId]);

  const listWearIndicators = useMemo(() => {
    if (state.assetType === 'ERS') {
      return [
        {
          label: 'Buried with Media',
          path: Undercarriage.Roller.WearIndicators.BuriedWithMedia,
          type: ['ERS'],
        },
        {
          label: 'Pedestal Damage / Weld Cracking',
          path: Undercarriage.Roller.WearIndicators.PedestalDamageWeldCracking,
          type: ['ERS'],
        },
        {
          label: 'Loose / Missing Retaining Hardware',
          path: Undercarriage.Roller.WearIndicators
            .LooseMissingRetainingHardware,
          type: ['ERS'],
        },
        {
          label: 'Slide Path Cracking / Delamination / Plastic Deformation',
          path: Undercarriage.Roller.WearIndicators
            .SlidePathCrackingDelaminationPlasticDeformation,
          type: ['ERS'],
        },
        {
          label: 'Severe Cracking',
          path: Undercarriage.Roller.WearIndicators.SevereCracking,
          type: ['ERS'],
        },
      ].map((el) => ({
        label: el.label,
        path: el.path,
      }));
    } else {
      return [
        {
          label: 'Buried with Media',
          path: Undercarriage.Roller.WearIndicators.BuriedWithMedia,
          type: ['HEX'],
        },
        {
          label: 'Pedestal Damage / Weld Cracking',
          path: Undercarriage.Roller.WearIndicators.PedestalDamageWeldCracking,
          type: ['HEX'],
        },
        {
          label: 'Loose / Missing Retaining Hardware',
          path: Undercarriage.Roller.WearIndicators
            .LooseMissingRetainingHardware,
          type: ['HEX'],
        },
        {
          label: 'Slide Path Cracking / Delamination / Plastic Deformation',
          path: Undercarriage.Roller.WearIndicators
            .SlidePathCrackingDelaminationPlasticDeformation,
          type: ['HEX'],
        },
        {
          label: 'Severe Cracking',
          path: Undercarriage.Roller.WearIndicators.SevereCracking,
          type: ['HEX'],
        },
      ].map((el) => ({
        label: el.label,
        path: el.path,
      }));
    }
  }, [state.assetType]);

  const markAsComplete = useCallback(async () => {
    try {
      if (!equipmentPart) {
        throw new Error('Empty equipment part');
      }

      if (!externalId) {
        throw new Error('Empty inspection');
      }

      if (!inspectionCategoryTypeId) {
        throw new Error('Empty category id');
      }

      await observationService.upsertObservation({
        inspectionId: externalId,
        inspectionCategoryId: inspectionCategoryTypeId,
        entityId: equipmentPart.id,
        entityExternalId: equipmentPart.externalId,
        dataLevel: ObservableLevelsEnum.equipmentPart,
        path: SliderRollerAssemblyConstants.completedPath,
        value: DateHelpers.getUTCDate().toISOString(),
        unitId: -1,
        actionTypeId: -1,
      });
    } catch (e) {
      console.log(`sliderScreen.error:${e}`);
    }
  }, [equipmentPart, inspectionCategoryTypeId, externalId]);

  return {
    error: state.error,
    markAsComplete,
    listWearIndicators,
  };
};
